import { z } from 'zod';
import type { CountryCode } from './country.js';

/**
 * F08 sports-profile types + Zod schemas.
 *
 * Decisions: see plans/08_F08_sports_profile.md (Decisions 1, 5, 9, 12).
 *
 * ─── SportName ──────────────────────────────────────────────────────────────
 *
 * The 41-sport canonical list lives in `@bravemove/design-tokens/icons` as
 * `SPORT_NAMES` (driven by the `SPORT_REGISTRY` keys). The same set is also
 * encoded in the `sports_profile_sports_sport_name_valid` CHECK constraint in
 * Postgres. We mirror the literal union here so `@bravemove/types` stays a
 * foundation-free package with no design-tokens dep. Drift between the three
 * locations is caught by:
 *
 *   - `packages/design-tokens/test/icons.test.tsx` — asserts SPORT_NAMES.length === 41
 *   - the DB CHECK constraint — rejects unknown sport_name values at insert time
 *   - `packages/types/test/profile.schema.test.ts` — asserts `SportNameSchema`
 *     parses every name from the mirrored union (see Task 7)
 */
export type SportName =
  | 'padel'
  | 'tennis'
  | 'squash'
  | 'badminton'
  | 'pickleball'
  | 'table-tennis'
  | 'football-5'
  | 'football-7'
  | 'football-11'
  | 'basketball'
  | 'volleyball'
  | 'handball'
  | 'rugby'
  | 'water-polo'
  | 'taekwondo'
  | 'karate'
  | 'judo'
  | 'bjj'
  | 'kickboxing'
  | 'muay-thai'
  | 'boxing'
  | 'mma'
  | 'swimming'
  | 'jet-ski'
  | 'kayak'
  | 'gym'
  | 'crossfit'
  | 'yoga'
  | 'pilates'
  | 'dance'
  | 'cycling'
  | 'running'
  | 'hiking'
  | 'climbing'
  | 'skiing'
  | 'bowling'
  | 'billiards'
  | 'golf'
  | 'equestrian'
  | 'archery'
  | 'shooting'
  | 'futsal'
  | 'motocross'
  | 'paragliding'
  | 'karting'
  | 'paintball'
  | 'airsoft'
  | 'scuba-diving'
  | 'surfing'
  | 'kitesurfing'
  | 'windsurfing'
  | 'wakeboarding'
  | 'rafting'
  | 'horse-racing'
  | 'gymnastics'
  | 'fencing'
  | 'ice-skating'
  | 'skateboarding'
  | 'parkour'
  | 'petanque'
  | 'darts'
  // ─── Added (chess + adventure/coastal + Lebanese board games) ───
  | 'chess'
  | 'clay-shooting'
  | 'snowboard'
  | 'sailing'
  | 'backgammon'
  | 'cards';

/** Tuple form for runtime use (Zod enum + iteration). Keep in sync with `SportName`. */
export const SPORT_NAME_VALUES = [
  'padel',
  'tennis',
  'squash',
  'badminton',
  'pickleball',
  'table-tennis',
  'football-5',
  'football-7',
  'football-11',
  'basketball',
  'volleyball',
  'handball',
  'rugby',
  'water-polo',
  'taekwondo',
  'karate',
  'judo',
  'bjj',
  'kickboxing',
  'muay-thai',
  'boxing',
  'mma',
  'swimming',
  'jet-ski',
  'kayak',
  'gym',
  'crossfit',
  'yoga',
  'pilates',
  'dance',
  'cycling',
  'running',
  'hiking',
  'climbing',
  'skiing',
  'bowling',
  'billiards',
  'golf',
  'equestrian',
  'archery',
  'shooting',
  'futsal',
  'motocross',
  'paragliding',
  'karting',
  'paintball',
  'airsoft',
  'scuba-diving',
  'surfing',
  'kitesurfing',
  'windsurfing',
  'wakeboarding',
  'rafting',
  'horse-racing',
  'gymnastics',
  'fencing',
  'ice-skating',
  'skateboarding',
  'parkour',
  'petanque',
  'darts',
  'chess',
  'clay-shooting',
  'snowboard',
  'sailing',
  'backgammon',
  'cards',
] as const satisfies readonly SportName[];

export const SportNameSchema = z.enum(SPORT_NAME_VALUES);

// ─── F31 Sport metadata — supportsDraws flag ────────────────────────────────

/**
 * F31 Decision 4 — Sport-aware draw support. The `match_outcomes.winning_side`
 * CHECK admits `'draw'` only for sports flagged `true` here; the service layer
 * rejects draws on other sports with `422 INVALID_DRAW`.
 *
 * Initial set: football (all variants), basketball, volleyball, handball, rugby
 * — sports where league rules typically allow regulation-time draws. Everything
 * else defaults to `false` (one-winner semantics like padel, tennis, BJJ).
 *
 * Hardcoded const at MVP per F31 Decision 4 / plan §4.3. A future admin
 * sport-catalog page can flip this to a DB-backed flag without changing
 * consumers — the lookup signature stays `(sportName: SportName) => boolean`.
 */
export const SPORT_SUPPORTS_DRAWS: Readonly<Record<SportName, boolean>> = Object.freeze({
  padel: false,
  tennis: false,
  squash: false,
  badminton: false,
  pickleball: false,
  'table-tennis': false,
  'football-5': true,
  'football-7': true,
  'football-11': true,
  basketball: true,
  volleyball: true,
  handball: true,
  rugby: true,
  'water-polo': true,
  taekwondo: false,
  karate: false,
  judo: false,
  bjj: false,
  kickboxing: false,
  'muay-thai': false,
  boxing: false,
  mma: false,
  swimming: false,
  'jet-ski': false,
  kayak: false,
  gym: false,
  crossfit: false,
  yoga: false,
  pilates: false,
  dance: false,
  cycling: false,
  running: false,
  hiking: false,
  climbing: false,
  skiing: false,
  bowling: false,
  billiards: false,
  golf: false,
  equestrian: false,
  archery: false,
  shooting: false,
  // Added sports — futsal allows regulation draws (football); the rest are one-winner.
  futsal: true,
  motocross: false,
  paragliding: false,
  karting: false,
  paintball: false,
  airsoft: false,
  'scuba-diving': false,
  surfing: false,
  kitesurfing: false,
  windsurfing: false,
  wakeboarding: false,
  rafting: false,
  'horse-racing': false,
  gymnastics: false,
  fencing: false,
  'ice-skating': false,
  skateboarding: false,
  parkour: false,
  petanque: false,
  darts: false,
  // chess allows regulation draws; the rest are one-winner / non-draw.
  chess: true,
  'clay-shooting': false,
  snowboard: false,
  sailing: false,
  backgammon: false,
  cards: false,
});

// ─── Privacy model (Decision 1) ─────────────────────────────────────────────

/**
 * F08 ships only the binary `'public' | 'private'` visibility. The Phase-2
 * friend graph / team membership may later extend this union with
 * `'teammates'` (Decision 2) — the projection helper learns to read a new
 * viewer-relationship input, no migration required.
 */
export type PrivacyValue = 'public' | 'private';

export const PrivacyValueSchema = z.enum(['public', 'private']);

/**
 * Closed enum of privacy-controlled field keys. Adding a new key is a Zod-
 * validation change here + a backfill migration on `sports_profiles.privacy_settings`.
 * Unknown keys are rejected by the API with `422 INVALID_PRIVACY_KEY`.
 */
export type PrivacyKey =
  | 'bio'
  | 'avatar_url'
  | 'home_city'
  | 'preferred_play_radius'
  | 'play_frequency'
  | 'availability_bands'
  | 'skill_levels'
  | 'stats'
  | 'match_history'
  | 'badges'
  | 'phone';

export type PrivacySettings = Readonly<Record<PrivacyKey, PrivacyValue>>;

/**
 * Strict Zod schema — every key MUST be present (no partial objects) and
 * unknown keys are rejected. Use for full-replacement writes from the API.
 */
export const PrivacySettingsSchema = z
  .object({
    bio: PrivacyValueSchema,
    avatar_url: PrivacyValueSchema,
    home_city: PrivacyValueSchema,
    preferred_play_radius: PrivacyValueSchema,
    play_frequency: PrivacyValueSchema,
    availability_bands: PrivacyValueSchema,
    skill_levels: PrivacyValueSchema,
    stats: PrivacyValueSchema,
    match_history: PrivacyValueSchema,
    badges: PrivacyValueSchema,
    phone: PrivacyValueSchema,
  })
  .strict();

/**
 * Partial-update variant — used when the client wants to flip a subset of
 * keys via `PATCH /me/profile`. Unknown keys still rejected; missing keys
 * mean "leave existing value untouched".
 */
export const PrivacySettingsPatchSchema = z
  .object({
    bio: PrivacyValueSchema.optional(),
    avatar_url: PrivacyValueSchema.optional(),
    home_city: PrivacyValueSchema.optional(),
    preferred_play_radius: PrivacyValueSchema.optional(),
    play_frequency: PrivacyValueSchema.optional(),
    availability_bands: PrivacyValueSchema.optional(),
    skill_levels: PrivacyValueSchema.optional(),
    stats: PrivacyValueSchema.optional(),
    match_history: PrivacyValueSchema.optional(),
    badges: PrivacyValueSchema.optional(),
    phone: PrivacyValueSchema.optional(),
  })
  .strict();

/**
 * The defaults written to every new `sports_profiles` row. Mirrors the DB
 * default in `packages/db/src/schema.ts` so the API can build the same shape
 * client-side without round-tripping.
 */
export const DEFAULT_PRIVACY_SETTINGS: PrivacySettings = Object.freeze({
  bio: 'public',
  avatar_url: 'public',
  home_city: 'public',
  preferred_play_radius: 'public',
  play_frequency: 'public',
  availability_bands: 'public',
  skill_levels: 'public',
  stats: 'private',
  match_history: 'private',
  badges: 'public',
  phone: 'private',
});

// ─── Privacy key ↔ field mapping (Task 0 follow-up — code-quality Issue 5) ───

/**
 * Mapping from each snake_case `PrivacyKey` to the camelCase field name(s) it
 * gates on the profile shapes.
 *
 * Single source of truth for `applyPrivacyProjection`. When you add a new
 * privacy-controlled field:
 *   1. Extend `PrivacyKey` above
 *   2. Add an entry here pointing at the camelCase field(s)
 *   3. Add a default to `DEFAULT_PRIVACY_SETTINGS`
 *   4. Add a column to `sports_profiles` (or join — e.g. `phone` lives on
 *      `users`) and bump the DB default jsonb
 *
 * Categories:
 *   - root.*    — gates fields on the profile-root shape returned to viewers
 *   - sport.*   — gates per-sport columns on `sports_profile_sports`
 *   - badges    — gates the entire `badges` array
 *   - match_history — reserved (no F08 field yet; toggle is respected, gate
 *     is empty until F31 ships the match-history surface)
 */
export const PRIVACY_KEY_TO_FIELDS: Readonly<
  Record<
    PrivacyKey,
    Readonly<{
      readonly root?: readonly (keyof SportsProfileRoot | 'phone')[];
      /** Field names on `SportsProfileSport` that are hidden when this key is 'private'. */
      readonly sport?: readonly (keyof SportsProfileSport)[];
      /** Special-case: the entire `badges` collection is hidden when 'private'. */
      readonly badges?: boolean;
      /** Reserved gate — currently F08 ships no match-history surface. */
      readonly matchHistory?: boolean;
    }>
  >
> = Object.freeze({
  bio: { root: ['bio'] as const },
  avatar_url: { root: ['avatarUrl'] as const },
  home_city: { root: ['homeGovernorateIso', 'homeCityCode'] as const },
  preferred_play_radius: { root: ['preferredPlayRadiusKm'] as const },
  play_frequency: { root: ['playFrequency'] as const },
  availability_bands: { root: ['availabilityBands'] as const },
  skill_levels: {
    sport: [
      'claimedSkillValue',
      'effectiveSkillValue',
      'skillSource',
      'skillLastUpdatedAt',
    ] as const,
  },
  stats: {
    sport: [
      'matchesPlayed',
      'wins',
      'losses',
      'draws',
      'hoursPlayed',
      'mvpVotesReceived',
      'noShowCount',
    ] as const,
  },
  match_history: { matchHistory: true },
  badges: { badges: true },
  phone: { root: ['phone'] as const },
});

// ─── Skill model (Decision 5) ───────────────────────────────────────────────

export type SkillSource = 'self' | 'match_engine' | 'admin_verified';

export const SkillSourceSchema = z.enum(['self', 'match_engine', 'admin_verified']);

// ─── Play frequency (Decision 1 / 20) ───────────────────────────────────────

export type PlayFrequency = 'weekly_1' | 'weekly_2_3' | 'weekly_4_plus' | 'monthly_few' | 'rarely';

export const PlayFrequencySchema = z.enum([
  'weekly_1',
  'weekly_2_3',
  'weekly_4_plus',
  'monthly_few',
  'rarely',
]);

// ─── Availability bands ─────────────────────────────────────────────────────

/**
 * Free-form boolean map for now. A closed shape (e.g. `weekday_evenings`,
 * `weekend_mornings`) lands in a Phase-2 refinement once the founder picks
 * the canonical bucket set; until then the schema accepts any string key
 * with a boolean value so the UI can iterate without a migration.
 *
 * TODO(F08-phase2): replace with a closed enum once UX validates the bands.
 */
export type AvailabilityBands = Readonly<Record<string, boolean>>;

export const AvailabilityBandsSchema = z.record(z.string(), z.boolean());

// ─── Sports profile sport row (Decision 5 + 9) ──────────────────────────────

/**
 * The row shape returned by the API. `claimedSkillValue` and
 * `effectiveSkillValue` come back from Postgres as strings (numeric driver
 * behaviour) but the service layer coerces them to numbers before the
 * boundary — the public type is `number`.
 */
export interface SportsProfileSport {
  readonly id: string;
  readonly userId: string;
  readonly sport: SportName;
  readonly claimedSkillValue: number;
  readonly effectiveSkillValue: number;
  readonly skillSource: SkillSource;
  readonly skillLastUpdatedAt: string;
  readonly yearsPlaying: number | null;
  readonly isPrimary: boolean;
  readonly matchesPlayed: number;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
  readonly hoursPlayed: number;
  readonly mvpVotesReceived: number;
  readonly noShowCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Input body for `POST /me/profile/sports`. */
export interface SportsProfileSportInput {
  readonly sport: SportName;
  readonly claimedSkillValue: number;
  readonly yearsPlaying?: number;
  readonly isPrimary?: boolean;
}

/**
 * Skill range: claim is 1.0–5.0; effective extends to 7.0 but only the admin
 * verify route can write above 5.0 at F08 (F31 unlocks `match_engine` later).
 * The schema rejects values outside the claim range here; the service layer
 * is the one place that lifts to 7.0.
 */
export const SportsProfileSportInputSchema = z
  .object({
    sport: SportNameSchema,
    claimedSkillValue: z
      .number()
      .min(1.0)
      .max(5.0)
      .refine((v) => Number.isFinite(v) && Math.round(v * 10) === v * 10, {
        message: 'claimedSkillValue must be a value with at most one decimal place',
      }),
    yearsPlaying: z.number().int().nonnegative().optional(),
    isPrimary: z.boolean().optional(),
  })
  .strict();

/** Input body for `PATCH /me/profile/sports/:sport`. All fields optional. */
export interface SportsProfileSportPatchInput {
  readonly claimedSkillValue?: number;
  readonly yearsPlaying?: number | null;
  readonly isPrimary?: boolean;
}

export const SportsProfileSportPatchSchema = z
  .object({
    claimedSkillValue: z
      .number()
      .min(1.0)
      .max(5.0)
      .refine((v) => Number.isFinite(v) && Math.round(v * 10) === v * 10, {
        message: 'claimedSkillValue must be a value with at most one decimal place',
      })
      .optional(),
    yearsPlaying: z.number().int().nonnegative().nullable().optional(),
    isPrimary: z.boolean().optional(),
  })
  .strict();

// ─── User badge (Decision 12) ───────────────────────────────────────────────

export type BadgeCategory = 'sport' | 'skill' | 'award' | 'milestone';

export const BadgeCategorySchema = z.enum(['sport', 'skill', 'award', 'milestone']);

/**
 * Row shape returned by the API. `attributedTrainerUserId` + `attributionLabel`
 * are reserved for F37 trainer endorsements (Decision 12); F08 reads but does
 * not write them.
 */
export interface UserBadge {
  readonly id: string;
  readonly userId: string;
  readonly badgeCode: string;
  readonly badgeName: string;
  readonly badgeCategory: BadgeCategory;
  readonly attributedTrainerUserId: string | null;
  readonly attributionLabel: string | null;
  readonly awardedAt: string;
  readonly revokedAt: string | null;
}

// ─── Profile root + viewer projections (Decision 3 + 21) ────────────────────

/**
 * Profile-root columns (minus `user_id`, which sits in the parent shape).
 * Used as the patch target on `PATCH /me/profile`.
 *
 * `phone` is joined from `users.phone` by the service layer (it doesn't live on
 * `sports_profiles`). It's surfaced here so the privacy projection helper —
 * gated by `privacy_settings.phone` — is the single source of truth for
 * visibility. Always present on the owner shape, optional on the viewer shape.
 */
export interface SportsProfileRoot {
  readonly countryCode: CountryCode;
  readonly bio: string | null;
  readonly avatarUrl: string | null;
  readonly homeGovernorateIso: string | null;
  readonly homeCityCode: string | null;
  readonly preferredPlayRadiusKm: number | null;
  readonly playFrequency: PlayFrequency | null;
  readonly availabilityBands: AvailabilityBands | null;
  readonly phone: string | null;
  readonly privacySettings: PrivacySettings;
  readonly onboardingCompletedAt: string | null;
}

/**
 * Patch body for `PATCH /me/profile`. Every field optional; unknown keys
 * rejected. `privacySettings` uses the partial-update variant so the client
 * can flip a single toggle without resending the full map.
 */
export const SportsProfileRootPatchSchema = z
  .object({
    bio: z.string().max(280).nullable().optional(),
    avatarUrl: z.string().url().nullable().optional(),
    homeGovernorateIso: z.string().nullable().optional(),
    homeCityCode: z.string().nullable().optional(),
    preferredPlayRadiusKm: z.number().int().min(1).max(50).nullable().optional(),
    playFrequency: PlayFrequencySchema.nullable().optional(),
    availabilityBands: AvailabilityBandsSchema.nullable().optional(),
    privacySettings: PrivacySettingsPatchSchema.optional(),
  })
  .strict();

export type SportsProfileRootPatch = z.infer<typeof SportsProfileRootPatchSchema>;

/**
 * What the OWNER sees — every field always present. Used by `GET /me/profile`.
 *
 * F09: `verified` and `verifiedAt` are always included regardless of
 * `privacy_settings` — the blue tick is a public trust signal (Decision 10).
 */
export interface SportsProfileFull extends SportsProfileRoot {
  readonly userId: string;
  readonly displayName: string;
  /** F09 v2 — the PLAYER tick (`users.verification_status = 'verified'`). */
  readonly verified: boolean;
  /** ISO timestamp when the blue tick was granted; null if not verified. Always public. */
  readonly verifiedAt: string | null;
  readonly sports: readonly SportsProfileSport[];
  readonly badges: readonly UserBadge[];
}

/**
 * What another signed-in user sees. Private fields are ABSENT (not nulled —
 * absent) per Decision 21. The projection helper omits keys whose
 * `privacy_settings[field] === 'private'`. Typed as `Partial<…>` over the
 * privacy-controlled subset; identity fields (`userId`, `displayName`,
 * `verified`, `countryCode`) are always present.
 *
 * For the `sports` array: when `privacy_settings.skill_levels === 'private'`
 * the array is still present (the user's sport memberships aren't secret) but
 * each row is projected to omit the skill-value columns. When
 * `privacy_settings.stats === 'private'` the counter columns are dropped from
 * each row in the same way. Both keys gate per-field — the array itself only
 * disappears when there are zero sports.
 */
export interface SportsProfileForViewer {
  readonly userId: string;
  readonly displayName: string;
  /** F09 v2 — the PLAYER tick (`users.verification_status = 'verified'`). */
  readonly verified: boolean;
  /**
   * F09: always present regardless of `privacy_settings` — the blue tick is a
   * public trust signal (Decision 10). ISO timestamp or null if not verified.
   */
  readonly verifiedAt: string | null;
  readonly countryCode: CountryCode;
  readonly bio?: string | null;
  readonly avatarUrl?: string | null;
  readonly homeGovernorateIso?: string | null;
  readonly homeCityCode?: string | null;
  readonly preferredPlayRadiusKm?: number | null;
  readonly playFrequency?: PlayFrequency | null;
  readonly availabilityBands?: AvailabilityBands | null;
  readonly phone?: string | null;
  /** Each sport row may itself be partially projected; see field-level comment above. */
  readonly sports?: readonly PartialSportsProfileSport[];
  /** Present when `privacy_settings.badges === 'public'`. */
  readonly badges?: readonly UserBadge[];
}

/**
 * A `SportsProfileSport` row that may have the skill-value and/or counter
 * columns omitted by the privacy projection. Identity columns (`id`, `userId`,
 * `sport`, `isPrimary`, `yearsPlaying`, timestamps) are always present.
 */
export type PartialSportsProfileSport = Pick<
  SportsProfileSport,
  'id' | 'userId' | 'sport' | 'isPrimary' | 'yearsPlaying' | 'createdAt' | 'updatedAt'
> &
  Partial<
    Pick<
      SportsProfileSport,
      | 'claimedSkillValue'
      | 'effectiveSkillValue'
      | 'skillSource'
      | 'skillLastUpdatedAt'
      | 'matchesPlayed'
      | 'wins'
      | 'losses'
      | 'draws'
      | 'hoursPlayed'
      | 'mvpVotesReceived'
      | 'noShowCount'
    >
  >;

/**
 * Anonymous (logged-out) projection per Decision 3. Flat shape served by
 * `GET /public/users/:userId/profile`.
 *
 * F09 added `verified` and `verifiedAt` — always public, no privacy gate.
 * The blue tick is a trust signal that benefits from being maximally visible.
 */
export interface SportsProfilePreview {
  readonly userId: string;
  readonly displayName: string;
  /** F09 v2 — the PLAYER tick (`users.verification_status = 'verified'`). */
  readonly verified: boolean;
  /** ISO timestamp when the blue tick was granted; null if not verified. */
  readonly verifiedAt: string | null;
  /** Included only if `privacy_settings.avatar_url === 'public'`. */
  readonly avatarUrl: string | null;
  readonly countryCode: CountryCode;
  readonly sports: ReadonlyArray<{
    readonly sport: SportName;
    readonly effectiveLevel: number;
    readonly isPrimary: boolean;
  }>;
}

// ─── Onboarding (Decision 11 / 20) ──────────────────────────────────────────

export interface OnboardingStatus {
  readonly completed: boolean;
  readonly completedAt: string | null;
}

// ─── User search (Decision 10 / 22) ─────────────────────────────────────────

export interface UserSearchQuery {
  readonly sport?: SportName;
  readonly minLevel?: number;
  readonly maxLevel?: number;
  readonly governorate?: string;
  readonly caza?: string;
  readonly q?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * Filter combination matrix (Decision 22):
 *   - `minLevel`/`maxLevel` REQUIRE `sport` (a skill range without a sport is
 *     meaningless; the API rejects with `422 LEVEL_RANGE_REQUIRES_SPORT`)
 *   - At least one of `sport`, `q`, `governorate` MUST be present (we don't
 *     surface a global "show me everyone" list)
 */
export const UserSearchQuerySchema = z
  .object({
    sport: SportNameSchema.optional(),
    minLevel: z.number().min(1.0).max(7.0).optional(),
    maxLevel: z.number().min(1.0).max(7.0).optional(),
    governorate: z.string().min(1).optional(),
    caza: z.string().min(1).optional(),
    q: z.string().min(1).max(64).optional(),
    limit: z.number().int().min(1).max(50).optional(),
    cursor: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if ((val.minLevel !== undefined || val.maxLevel !== undefined) && val.sport === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'minLevel/maxLevel require sport',
        path: ['sport'],
      });
    }
    if (val.minLevel !== undefined && val.maxLevel !== undefined && val.minLevel > val.maxLevel) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'minLevel must be <= maxLevel',
        path: ['minLevel'],
      });
    }
    if (val.sport === undefined && val.q === undefined && val.governorate === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'at least one of sport, q, governorate is required',
        path: ['sport'],
      });
    }
  });

export interface UserSearchResult {
  readonly userId: string;
  readonly displayName: string;
  /** F09 v2 — the PLAYER tick (`users.verification_status = 'verified'`). */
  readonly verified: boolean;
  readonly avatarUrl: string | null;
  readonly primarySport: SportName | null;
  /** Skill on the searched sport (or the user's top sport when no sport was specified). */
  readonly effectiveSkillValue: number | null;
  readonly homeGovernorateIso: string | null;
  readonly homeCityCode: string | null;
}

export interface UserSearchResponse {
  readonly items: readonly UserSearchResult[];
  readonly nextCursor: string | null;
}

// ─── Admin skill-verify body (Decision 17) ──────────────────────────────────

export interface AdminSkillVerifyInput {
  readonly sport: SportName;
  readonly effectiveSkillValue: number;
  readonly reason: string;
}

export const AdminSkillVerifyInputSchema = z
  .object({
    sport: SportNameSchema,
    effectiveSkillValue: z
      .number()
      .min(1.0)
      .max(7.0)
      .refine((v) => Number.isFinite(v) && Math.round(v * 10) === v * 10, {
        message: 'effectiveSkillValue must be a value with at most one decimal place',
      }),
    reason: z.string().min(10).max(500),
  })
  .strict();
